// Recovery from a poisoned JavaScript chunk in the browser's HTTP cache.
//
// `/assets/*` responses carry no `Content-Length`, so a transfer that gets cut
// short is stored as if it were complete. Because every Rollup chunk ends with
// its `export{...}` statement, a truncated body is valid JavaScript that
// exports nothing, and the chunks importing it fail to link. The engines word
// that differently, which is why the match below is a set of patterns rather
// than one string:
//
//   Safari  Importing binding name 't' is not found.
//   Chrome  The requested module './x.js' does not provide an export named 't'
//   Firefox import not found: t
//
// Reloading does not help on its own: the cached copy is what gets read again.
// The fix is to re-fetch the module URLs with `cache: 'reload'`, which bypasses
// the cache AND replaces the stored entry, and only then reload the document.

const ATTEMPT_KEY = 'anyapi:module-recovery';

const LINK_FAILURE = [
  /importing binding name/i,
  /does not provide an export named/i,
  /import not found/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /'?text\/html'? is not a valid javascript mime type/i,
];

/** True when `error` is a module that failed to load or link, not app logic. */
export function isModuleLinkFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return LINK_FAILURE.some((pattern) => pattern.test(message));
}

/**
 * Every same-origin module URL this document has already pulled, including the
 * lazily imported route chunks that no `<link rel="modulepreload">` names.
 * `performance` is the only place that remembers a dynamic import's URL.
 */
function loadedModuleUrls(): string[] {
  const urls = new Set<string>();
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"][href]')) {
    urls.add(link.href);
  }
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    urls.add(script.src);
  }
  for (const entry of performance.getEntriesByType('resource')) {
    if (entry.name.startsWith(`${location.origin}/assets/`) && entry.name.endsWith('.js')) {
      urls.add(entry.name);
    }
  }
  return [...urls];
}

/**
 * Re-fetch this document's modules past the cache, then reload once.
 *
 * Returns false when recovery is not available (server render) or when it has
 * already been tried in this tab, so the caller can show a real error instead
 * of reloading forever.
 */
export async function recoverFromModuleFailure(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(ATTEMPT_KEY)) return false;
    sessionStorage.setItem(ATTEMPT_KEY, '1');
  } catch {
    // Storage denied (private mode, blocked cookies). One reload with no
    // guard risks a loop, so decline rather than gamble.
    return false;
  }
  await Promise.all(
    loadedModuleUrls().map((url) => fetch(url, { cache: 'reload', credentials: 'same-origin' })),
  );
  location.reload();
  return true;
}
